import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Button } from '@/components/base/buttons/button'
import { api } from '@/shell/api/client'

export interface AuthMethods { password: boolean, google: boolean, googleDomains: string[] }

/** Which sign-in methods this instance offers (PRD-019 Q1). The Google button renders only when the API says so. */
export function useAuthMethods() {
  return useQuery({
    queryKey: ['auth-methods'],
    queryFn: () => api<AuthMethods>('/auth/methods'),
    staleTime: 5 * 60_000,
    retry: false,
  })
}

/** Plain messages for the `?error=` Better Auth appends when the Google callback is refused (lib/google.ts). */
export function googleErrorMessage(code: string | null, domains: string[] = []): string | null {
  if (!code)
    return null
  switch (code) {
    case 'domain_not_allowed':
      return domains.length
        ? `That Google account is not on an allowed domain. Use your ${domains.join(' or ')} account.`
        : 'That Google account is not on an allowed domain. Use your work account.'
    case 'no_seat':
      return 'That Google account has no seat in Gallop. Ask your CFO for an invite, or open the invite link you were sent.'
    case 'access_denied':
      return 'Google sign-in was canceled.'
    default:
      return 'Could not sign in with Google. Try again, or use your email and password.'
  }
}

/**
 * Google's "G" mark, per the Sign in with Google branding guidelines: the four-color logo at 18px on
 * a light button with a 1px border, label "Continue with Google". Inline SVG so no asset request.
 */
function GoogleMark({ className }: { className?: string }) {
  return (
    <svg className={className} width="18" height="18" viewBox="0 0 48 48" aria-hidden focusable="false">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  )
}

/**
 * "Continue with Google": asks the API for the Google authorization URL and follows it. The API
 * builds the URL with the state cookie, so the browser must be the one that navigates.
 */
export function GoogleButton({ callbackPath, errorPath, label = 'Continue with Google', onError }: { callbackPath: string, errorPath: string, label?: string, onError: (message: string) => void }) {
  const [busy, setBusy] = useState(false)
  const start = async () => {
    setBusy(true)
    try {
      const origin = window.location.origin
      const res = await api<{ url?: string, redirect?: boolean }>('/auth/sign-in/social', {
        method: 'POST',
        json: { provider: 'google', callbackURL: `${origin}${callbackPath}`, errorCallbackURL: `${origin}${errorPath}`, disableRedirect: true },
      })
      if (!res.url)
        throw new Error('no url')
      window.location.assign(res.url)
    }
    catch {
      setBusy(false)
      onError('Could not start Google sign-in. Try again, or use your email and password.')
    }
  }
  return (
    <Button type="button" color="secondary" size="lg" isLoading={busy} showTextWhileLoading iconLeading={GoogleMark} onClick={start} data-testid="google-signin">
      {label}
    </Button>
  )
}

/** "or" rule between the two methods. */
export function MethodDivider() {
  return (
    <div className="flex items-center gap-3 text-xs text-quaternary" aria-hidden>
      <span className="h-px flex-1 bg-border-secondary" />
      or
      <span className="h-px flex-1 bg-border-secondary" />
    </div>
  )
}
